import type { ComponentProps } from "react"
import {
  DashboardSquare01Icon,
  Invoice03Icon,
  CreditCardIcon,
  ElectricHome02Icon,
  CustomerSupportIcon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { Ticket as TicketIcon, type LucideIcon } from "lucide-react"
import type { NavItem } from "@/types/nav"

// Exported so other resident-facing surfaces (e.g. the AutoPay adoption
// banner) can apply the same bank-feature gating as the sidebar without
// duplicating the funding-source list.
export const FUNDING_SOURCES_WITH_BANK = [
  "resident_only",
  "first_resident_fallback_company",
]

// `NavItem.icon` is typed as `LucideIcon`, which the sidebar renders as
// `<item.icon className="..." />`. Hugeicons works as `<HugeiconsIcon icon={...} />`,
// so each top-level tab uses a small wrapper component that adapts the call
// shape. Keeping the wrappers local avoids a workspace-wide retype of
// `NavItem.icon` and matches how this codebase already mixes both libs.
function makeHugeiconNavIcon(
  hugeIcon: ComponentProps<typeof HugeiconsIcon>["icon"],
  displayName: string,
): LucideIcon {
  const Wrapped = (
    props: ComponentProps<typeof HugeiconsIcon>,
  ) => <HugeiconsIcon icon={hugeIcon} {...props} />
  Wrapped.displayName = displayName
  return Wrapped as unknown as LucideIcon
}

const DashboardNavIcon = makeHugeiconNavIcon(
  DashboardSquare01Icon,
  "DashboardNavIcon",
)
const BillsNavIcon = makeHugeiconNavIcon(Invoice03Icon, "BillsNavIcon")
const UtilitiesNavIcon = makeHugeiconNavIcon(
  ElectricHome02Icon,
  "UtilitiesNavIcon",
)
const PaymentMethodsNavIcon = makeHugeiconNavIcon(
  CreditCardIcon,
  "PaymentMethodsNavIcon",
)
const HelpNavIcon = makeHugeiconNavIcon(CustomerSupportIcon, "HelpNavIcon")

/**
 * Resident sidebar — flat 5-item IA (Pay Now pivot, May 2026; Help +
 * Concerns merged 2026-08).
 *
 * No collapsible groups. Every item is top-level. Order matches the
 * resident's primary intent: see status (Dashboard) → take action on bills
 * (Bills) → review history (Payments) → manage configuration (My
 * Utilities, Payment Methods) → get help (Help, which now includes the
 * resident's own reported issues under a "My Requests" tab).
 *
 * Items hidden from this build:
 *   - Statements         — folded into Bills (bill PDF column on rows).
 *   - Notifications      — UI not yet ready; Spike Alerts will live here.
 *   - Spike Alerts       — moving under Notifications when shipped.
 *   - Upcoming           — folded into Bills with the `scheduled` status filter.
 *   - My Concerns        — folded into Get Help as a "My Requests" tab.
 *
 * Bank-feature gating (Payments + Payment Methods) still applies based on
 * `company.funding_source` so resident-only / first-resident-fallback
 * properties show the financial surfaces and company_only properties hide
 * them.
 */
export function getResidentNav(
  fundingSource: string,
  isPrimary: boolean = true,
): NavItem[] {
  const showFinancials = FUNDING_SOURCES_WITH_BANK.includes(fundingSource)
  // Payment Methods needs primary-resident; the page itself shows a
  // read-only notice for secondaries, but we hide the sidebar entry to
  // keep the IA clean.
  const showBankFeatures = showFinancials && isPrimary

  // Order + group headings mirror the resident bills refresh prototype:
  //   Your home: Dashboard · Bills & Payments · My Utilities · Payment Methods
  //   Help:      Get Help · My Requests
  // `group` drives the SidebarGroupLabel headers rendered by NavMain; navs
  // without `group` (every other role) render flat, unchanged.
  const HOME = "Your home"
  const HELP = "Help"
  const nav: NavItem[] = [
    {
      title: "Dashboard",
      url: "/dashboard/resident/overview",
      icon: DashboardNavIcon,
      group: HOME,
    },
    {
      // Bills + Payments are one destination now — the standalone "Payments"
      // (funding-history) route is retired and redirects here.
      title: "Bills & Payments",
      url: "/dashboard/resident/bills",
      icon: BillsNavIcon,
      group: HOME,
    },
  ]

  nav.push({
    title: "My Utilities",
    url: "/dashboard/resident/services",
    icon: UtilitiesNavIcon,
    group: HOME,
  })

  if (showBankFeatures) {
    nav.push({
      title: "Payment Methods",
      url: "/dashboard/resident/payment-methods",
      icon: PaymentMethodsNavIcon,
      group: HOME,
    })
  }

  // Get Help — FAQs/contact only. The resident's own reported issues live on
  // their own top-level page ("My Requests"), no longer a tab on Get Help.
  nav.push({
    title: "Get Help",
    url: "/dashboard/resident/help",
    icon: HelpNavIcon,
    group: HELP,
  })
  nav.push({
    title: "My Requests",
    url: "/dashboard/resident/requests",
    icon: TicketIcon,
    group: HELP,
  })

  return nav
}

// Default export for backward compatibility (used by preload.ts etc.)
export const residentNav = getResidentNav("company_only")
